<script setup>
import { computed, reactive, ref, shallowRef } from 'vue'
import { DxDataGrid } from 'devextreme-vue/data-grid'
import { DxPopup, DxToolbarItem } from 'devextreme-vue/popup'
import { DxNumberBox } from 'devextreme-vue/number-box'
import { DxTextBox } from 'devextreme-vue/text-box'
import 'devextreme-vue/text-area'
import * as dataGrid from '@/composables/data-grid'
import { useColumns } from '@/composables/registry/warehouse-document-row-grid'
import { useDocumentRow } from '@/composables/document-row'
import { useDocumentRowStock } from '@/composables/document-row-stock'
import { getI18n } from '@/plugins/i18n'
import DepositDropDownBox from '@/components/shared/DepositDropDownBox.vue'
import ProductDropDownBox from '@/components/shared/ProductDropDownBox.vue'
import DocumentRowTypeToolbar from '@/components/shared/DocumentRowTypeToolbar.vue'
import DocumentRowStockInfo from '@/components/shared/DocumentRowStockInfo.vue'
import WarehouseDocumentRowStockInfo from '@/components/registry/WarehouseDocumentRowStockInfo.vue'
import DocumentRowOtherDeposits from '@/components/shared/DocumentRowOtherDeposits.vue'
import { usePortalStore } from '@/stores/portal.store'
import { useRegistryStore } from '@/stores/registry.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useProductService } from '@/services/product.service'
import CustomToolbarAddButtonTemplate from '@/components/shared/CustomToolbarAddButtonTemplate.vue'
import CustomToolbarDeleteButtonTemplate from '@/components/shared/CustomToolbarDeleteButtonTemplate.vue'
import EditLockButtonsTemplate from '@/components/shared/EditLockButtonsTemplate.vue'
import ImportRowIcon from '@/components/shared/ImportRowIcon.vue'

const props = defineProps({
  data: {
    type: Object,
    default: null,
  },
  documentType: {
    type: Number,
    default: null,
  },
  entityLock: {
    type: Object,
    required: true,
  },
})

const emits = defineEmits(['row-saved'])

const i18n = getI18n()
const portalStore = usePortalStore()
const registryStore = useRegistryStore()
const webSiteStore = useWebSiteStore()
const productService = useProductService()
const { gridRef, refreshGridAsync, focusGrid, setCellValueByField } = dataGrid.useGridRef()
const {
  documentRowType,
  getEditMainInfoColumns,
  getEditAdditionalInfoColumns,
  onDocumentRowInitNewRow,
  onDocumentRowEditorPreparing,
  onDocumentRowEditorPreparingSetIsReadOnly,
  onDocumentRowTypeChanged,
  onDocumentRowUpdated,
  setDocumentRowProductPurchaseFamily,
  focusQuantityEditor,
} = useDocumentRow(gridRef)

const currentDeposit = ref(null)
const currentDestinationDeposit = ref(null)
const { loadProductStocksAsync, stockInfo, otherDeposits, setStockPosition, positionChange } = useDocumentRowStock(registryStore, currentDeposit, currentDestinationDeposit)
const selectedRowData = ref([])
const supplierBrand = ref(null)
const keepDescription = ref(true)
const productUnitOfMeasure = ref(null)
const isToolbarDisabled = ref(false)
const editors = shallowRef({})
let pendingQuantityChange = null
const rowStockPreview = reactive({
  quantity: 0,
  hasProduct: false,
  confirmed: false,
  unitOfMeasureRef: null,
  productUnitOfMeasureRef: null,
  supplierBrandRef: null,
})
const isConfirmedRow = ref(false)
const confirmation = ref(null)
const confirmationQuantity = ref(null)
const confirmationSourceDeposit = ref(null)
const confirmationDestinationDeposit = ref(null)
const confirmationStocks = useDocumentRowStock(registryStore, confirmationSourceDeposit, confirmationDestinationDeposit)
const confirmationStockInfo = ref({})
const isPreviewingConfirmation = ref(false)
let confirmationPreviewRequest = 0
const isConfirming = ref(false)
const productDropDownRef = ref(null)
const currency = webSiteStore.currencies.find(i => i.id === props.data?.currency_ref) || webSiteStore.getDefaultCurrency()

const selectedRows = computed(() => selectedRowData.value.filter(row => row.document === props.data?.id))
const includesVat = computed(() => !!props.data?.includes_vat)
const isTransfer = computed(() => props.documentType === portalStore.documentTypesEnum.WAREHOUSE_TRANSFER)
const availableOtherDeposits = computed(() => otherDeposits.value.filter(deposit => !isTransfer.value || deposit.id !== currentDestinationDeposit.value))
const canAddRow = computed(() => !!props.data?.id && (!props.entityLock.isLocked(false, props.data.id) || props.entityLock.isLocked(true, props.data.id)))
const canEditRow = computed(() => canAddRow.value && !isConfirmedRow.value)
const canEditStockPosition = computed(() => canEditRow.value && props.documentType === portalStore.documentTypesEnum.WAREHOUSE_LOAD && !!stockInfo.value.id)
const confirmationReady = computed(() =>
  !!confirmation.value?.token &&
  !isPreviewingConfirmation.value &&
  confirmationQuantity.value !== '' &&
  confirmationQuantity.value != null &&
  Number(confirmationQuantity.value) === Number(confirmation.value.quantity)
)

const confirmationStockChanges = computed(() => {
  const changes = {}
  if (!confirmationReady.value) return changes

  for (const movement of confirmation.value.movements) {
    const suffix = movement.deposit === confirmationSourceDeposit.value ? '' : '_secondary'
    const quantity = Number(movement.quantity)
    changes[`stock${suffix}`] = (changes[`stock${suffix}`] || 0) + quantity
    changes[`theoreticalStock${suffix}`] = (changes[`theoreticalStock${suffix}`] || 0) + quantity
  }

  return changes
})

const dataSource = dataGrid.useODataSource(
  registryStore.api.getAbsoluteUrl() + registryStore.api.warehouseDocumentRowsUrl,
  { customParamsFn: () => ({ $notop: true, document: props.data?.id || 0 }), avoidDuplicateDatasourceIds: true },
)

const columns = useColumns(
  supplierBrand,
  keepDescription,
  currency,
  documentRowType,
  productUnitOfMeasure,
  includesVat,
  value => { currentDeposit.value = value },
  true,
  { isTransfer, onDestinationDepositChanged: value => { currentDestinationDeposit.value = value } },
)

const commitQuantityChange = () => {
  const pending = pendingQuantityChange
  pendingQuantityChange = null
  pending?.commit?.(pending.args)
}

const depositLabel = id => {
  const deposit = registryStore.getLocalizedDeposits().find(item => item.id === id)
  return deposit ? `${deposit.code} - ${deposit.name}` : ''
}

const previewConfirmation = async(row) => {
  if (!canAddRow.value || row.confirmed_at || isConfirming.value) return

  isConfirming.value = true
  const documentId = props.data.id

  try {
    confirmationSourceDeposit.value = row.deposit || props.data.deposit
    confirmationDestinationDeposit.value = row.garage || props.data.destination_deposit
    const [response] = await Promise.all([
      registryStore.api.previewWarehouseDocumentRowConfirmationAsync(row.id),
      confirmationStocks.loadProductStocksAsync(row.product_ref, row.product_cost),
    ])

    if (props.data?.id === documentId) {
      confirmationStockInfo.value = { ...confirmationStocks.stockInfo.value }
      confirmationQuantity.value = Number(response.data.quantity)
      isPreviewingConfirmation.value = false
      confirmationPreviewRequest++
      confirmation.value = response.data
    }
  } finally {
    isConfirming.value = false
  }
}

const updateConfirmationQuantity = async(value) => {
  confirmationQuantity.value = value
  const request = ++confirmationPreviewRequest

  if (value === '' || value == null || !Number.isFinite(Number(value)) || Number(value) < 0) {
    isPreviewingConfirmation.value = false
    return
  }

  const rowId = confirmation.value?.id
  if (!rowId) return

  isPreviewingConfirmation.value = true
  try {
    const response = await registryStore.api.previewWarehouseDocumentRowConfirmationAsync(rowId, value)
    if (request === confirmationPreviewRequest && confirmation.value?.id === rowId) confirmation.value = response.data
  } catch {
    if (request === confirmationPreviewRequest && confirmation.value?.id === rowId) confirmation.value.token = null
  } finally {
    if (request === confirmationPreviewRequest) isPreviewingConfirmation.value = false
  }
}

const confirmRow = async() => {
  if (!confirmationReady.value || isConfirming.value || !canAddRow.value) return

  isConfirming.value = true
  const documentId = props.data.id

  try {
    await registryStore.api.confirmWarehouseDocumentRowAsync(confirmation.value.id, confirmation.value.token)
    confirmation.value = null
    emits('row-saved', documentId)
    await refreshGridAsync()
  } finally {
    isConfirming.value = false
  }
}

const onConfirmationHiding = e => {
  if (isConfirming.value && confirmation.value) e.cancel = true
  else confirmation.value = null
}

const cancelConfirmationButton = computed(() => ({
  text: i18n.t('common.cancel'),
  disabled: isConfirming.value,
  onClick: () => { confirmation.value = null },
}))

const confirmConfirmationButton = computed(() => ({
  text: i18n.t('common.confirm'),
  type: 'default',
  disabled: isConfirming.value || !canAddRow.value || !confirmationReady.value,
  onClick: confirmRow,
}))

const getEditColumns = (rowType) => {
  return [
    {
      itemType: 'group',
      colSpan: 2,
      items: [
        {
          itemType: 'group',
          caption: i18n.t('common.mainInfo'),
          colCount: 12,
          name: 'mdi-view-list',
          captionTemplate: 'row-type-caption-template',
          items: getEditMainInfoColumns(rowType, dataGrid, null, true),
        },
        ...(rowType === webSiteStore.documentRowTypesEnum.TEXT ? [] : [{
          itemType: 'group',
          caption: i18n.t('common.additionalInfo'),
          colCount: 4,
          name: 'mdi-currency-usd-circle',
          captionTemplate: 'generic-caption-template',
          cssClass: 'my-form-wrapper',
          items: getEditAdditionalInfoColumns(rowType, currency, dataGrid),
        }]),
      ],
    },
    {
      itemType: 'tabbed',
      colSpan: 3,
      tabs: [
        {
          title: i18n.t('common.stock'),
          items: [
            {
              itemType: 'group',
              caption: i18n.t('common.stock'),
              colCount: isTransfer.value ? 17 : 1,
              items: [
                {
                  dataField: 'deposit',
                  colSpan: isTransfer.value ? 8 : 1,
                  label: { text: i18n.t(isTransfer.value ? 'warehouseDocument.sourceDeposit' : 'common.deposit') },
                },
                ...(isTransfer.value ? [
                  {
                    template: 'depositTransferArrowTemplate',
                    label: { text: '\u00a0', visible: true, showColon: false },
                  },
                  { dataField: 'garage', colSpan: 8, label: { text: i18n.t('warehouseDocument.destinationDeposit') } },
                ] : []),
              ],
            },
            {
              itemType: 'group',
              caption: i18n.t('common.info', 2),
              colCount: 2,
              items: [
                { template: 'stockInformationTemplate', colSpan: 2 },
                { template: 'otherDepositsTemplate', colSpan: 2 },
              ],
            },
          ],
        },
      ],
    },
  ]
}

const editing = dataGrid.useDefaultEditing(`${i18n.t('common.add')} ${i18n.t('common.row')}`, {
  colCount: 5,
  items: getEditColumns(documentRowType.value),
}, {
  width: '90vw',
  isSaveAndNew: true,
  entityLock: props.entityLock,
  onInitializedCustom: e => props.entityLock.readOnlyToolbarItems(e, () => canEditRow.value),
})

editing.popup.onShown = () => { if (dataGrid.isAdding.value) productDropDownRef.value?.focus?.() }

editing.popup.toolbarItems?.slice(0, -1).forEach(item => {
  const onClick = item.options.onClick

  item.options.onClick = e => {
    commitQuantityChange()
    onClick(e)
  }
})

const editingOptions = computed(() => ({ ...editing, allowAdding: canAddRow.value }))

const rowButtons = props.entityLock.getEntityLockButtons({
  visible: {
    edit: e => !e.data?.confirmed_at,
    delete: e => !e.data?.confirmed_at,
    view: e => !!e.data?.confirmed_at,
  },
})

const onInitNewRow = (e) => {
  pendingQuantityChange = null
  isConfirmedRow.value = false

  if (!canAddRow.value) {
    e.component.cancelEditData()
    return
  }

  loadProductStocksAsync(null)
  productUnitOfMeasure.value = null
  supplierBrand.value = registryStore.supplierBrands.find(i => i.id === registryStore.companyData.default_supplier_brand_ref) || null
  e.data.document = props.data.id
  e.data.document_row_type = webSiteStore.documentRowTypesEnum.PRODUCT
  e.data.vat_type_ref = props.data.vat_type_ref || registryStore.companyData.default_vat_type_ref
  e.data.vat_type_percentage = webSiteStore.vatTypes.find(v => v.id === e.data.vat_type_ref)?.percentage
  onDocumentRowInitNewRow(e, supplierBrand, 0, isToolbarDisabled, props.data.deposit, isTransfer.value ? props.data.destination_deposit : null)
  e.data.discount = e.data.discount_2 = e.data.discount_3 = 0
  e.data.total_no_vat = e.data.total_vat = e.data.total_discount = 0
  currentDeposit.value = e.data.deposit
  currentDestinationDeposit.value = e.data.garage

  Object.assign(rowStockPreview, {
    quantity: e.data.quantity,
    hasProduct: false,
    confirmed: false,
    unitOfMeasureRef: null,
    productUnitOfMeasureRef: null,
    supplierBrandRef: e.data.supplier_brand_ref,
  })

  onDocumentRowTypeChanged(documentRowType.value, getEditColumns)
}

const onEditorPreparing = (e) => {
  onDocumentRowEditorPreparing(e, isToolbarDisabled, supplierBrand, includesVat)

  if (e.parentType === 'dataRow') {
    e.editorOptions.onInitialized = arg => { editors.value[e.dataField] = arg.component }

    if (['quantity', 'expected_quantity'].includes(e.dataField)) {
      const onValueChanged = e.editorOptions.onValueChanged
      const onFocusOut = e.editorOptions.onFocusOut

      e.editorOptions.valueChangeEvent = 'input change'
      e.editorOptions.onValueChanged = args => {
        if (args.event) {
          pendingQuantityChange = { commit: onValueChanged, args }
          if (e.dataField === 'quantity') rowStockPreview.quantity = args.value
        }
      }
      e.editorOptions.onFocusOut = args => {
        commitQuantityChange()
        onFocusOut?.(args)
      }
    }

    if (e.dataField === 'unit_of_measure_ref') {
      const onValueChanged = e.editorOptions.onValueChanged

      e.editorOptions.onValueChanged = args => {
        onValueChanged?.(args)
        rowStockPreview.unitOfMeasureRef = args.value
      }
    }

    if (e.dataField === 'product_ref') e.editorOptions.disabled = !!e.row.data?.id || typeof e.row.data?.supplier_brand_ref !== 'number'
    if (e.dataField === 'product_price') e.editorOptions.readOnly = true
  }

  onDocumentRowEditorPreparingSetIsReadOnly(e, !canEditRow.value, editors)
}

const onEditingStart = (e) => {
  pendingQuantityChange = null
  isConfirmedRow.value = !!e.data.confirmed_at
  documentRowType.value = e.data.document_row_type
  setDocumentRowProductPurchaseFamily(e.data)
  supplierBrand.value = registryStore.supplierBrands.find(i => i.id === e.data.supplier_brand_ref) || null
  productUnitOfMeasure.value = e.data.unit_of_measure_ref
  currentDeposit.value = e.data.deposit
  currentDestinationDeposit.value = e.data.garage

  Object.assign(rowStockPreview, {
    quantity: e.data.quantity,
    hasProduct: !!e.data.product_ref,
    confirmed: !!e.data.confirmed_at,
    unitOfMeasureRef: e.data.unit_of_measure_ref,
    productUnitOfMeasureRef: e.data.product_unit_of_measure_ref || e.data.unit_of_measure_ref,
    supplierBrandRef: e.data.supplier_brand_ref,
  })

  productService.updatePriceVatValue(e.data, e.data)
  productService.updateTotalDiscountValue(e.data, e.data)
  onDocumentRowTypeChanged(documentRowType.value, getEditColumns)
  loadProductStocksAsync(e.data.product_ref, e.data.product_cost)
}

const onDocumentRowTypeSelect = (rowType) => {
  loadProductStocksAsync(null)
  documentRowType.value = rowType
  supplierBrand.value = null
  setCellValueByField('document_row_type', rowType)
  onDocumentRowTypeChanged(rowType, getEditColumns)
}

const onProductValueChanged = (e) => {
  Object.assign(rowStockPreview, {
    hasProduct: !!e.value?.id,
    productUnitOfMeasureRef: e.value?.unit_of_measure_ref,
    unitOfMeasureRef: e.value?.unit_of_measure_ref,
    supplierBrandRef: e.value?.supplier_brand_ref,
  })

  const editingRow = gridRef.value?.instance.getVisibleRows().find(row => row.isEditing)
  if (editingRow) rowStockPreview.quantity = editingRow.data.quantity

  loadProductStocksAsync(e.value?.id, e.value?.cost)
  if (!e.value) {
    keepDescription.value = false
    supplierBrand.value = null
  }
  setDocumentRowProductPurchaseFamily(e.value)

  if (e.value) { focusQuantityEditor(editors) }
}

const onOtherDepositSelected = (deposit) => {
  if (!canEditRow.value || (isTransfer.value && deposit.id === currentDestinationDeposit.value)) return
  setCellValueByField('deposit', deposit)
  currentDeposit.value = deposit.id
}

const onStockPositionChanged = (position) => {
  if (!canEditStockPosition.value) return
  setStockPosition(position ?? '')
  setCellValueByField('stock_position', position ?? '')
}

const onSaving = (e) => {
  const change = e.changes.find(change => ['insert', 'update'].includes(change.type))
  if (!change) return
  delete change.data.stock_position
  if (!canEditStockPosition.value || positionChange.value?.id !== stockInfo.value.id) return
  e.promise = registryStore.api.updateProductStockAsync(stockInfo.value.id, { position: positionChange.value.position })
}

const onBatchDeleteDocumentRowsAsync = async(ids) => {
  await registryStore.api.batchDeleteWarehouseDocumentRowsAsync(ids)
  emits('row-saved', props.data?.id)
}

const refreshGridAfterImport = async() => {
  await refreshGridAsync()
  focusGrid()
  emits('row-saved', props.data?.id)
}

const parentId = computed(() => props.data?.id)
const { onDragEnd } = dataGrid.useDefaultRowReordering(
  registryStore.api.reorderWarehouseDocumentRowsAsync,
  parentId,
  gridRef,
  () => !canAddRow.value || props.data?.confirmed_rows_count > 0,
)

const gridOptions = dataGrid.useDefaultGridOptions({
  id: 'warehouse-document-row-grid',
  entityLock: props.entityLock,
  entityLockId: () => props.data?.id,
  mode: 'row',
  columns,
  editing,
  repaintChangesOnly: true,
  selection: dataGrid.useDefaultGridSelection(),
  onToolbarPreparing: (e) => {
    dataGrid.useDefaultToolbar(e, { allowBatchDelete: true })
    e.toolbarOptions.items.unshift({ location: 'after', template: 'importToolbarItem' })
  },
  onSelectionChanged: e => { selectedRowData.value = e.selectedRowsData },
  onInitNewRow,
  onEditorPreparing,
  onEditingStart,
  onEditCanceled: () => {
    pendingQuantityChange = null
    loadProductStocksAsync(null)
  },
  onRowUpdated: onDocumentRowUpdated,
  onSaving,
  onRowInserted: onDocumentRowUpdated,
  onSaved: () => emits('row-saved', props.data?.id),
  rowDragging: { allowReordering: true, onDragEnd },
  focusGrid,
  disabledFocusOnContentReady: true,
  splittedGridDetail: true,
})
</script>

<template>
  <dx-data-grid
    ref="gridRef"
    v-bind="gridOptions"
    :data-source="dataSource"
    :editing="editingOptions"
    class="q-pr-lg"
  >
    <template #importToolbarItem>
      <import-row-icon
        :disabled="!canAddRow"
        :document-type="props.documentType"
        :document-id="props.data?.id"
        :entity-lock="props.entityLock"
        :options="{ allowedDocumentRowTypes: [webSiteStore.documentRowTypesEnum.PRODUCT] }"
        @updated="refreshGridAfterImport"
      />
    </template>
    <template #customToolbarAddButtonTemplate>
      <custom-toolbar-add-button-template
        v-if="canAddRow"
        :master-entity-id="props.data.id"
        :grid="gridRef"
        :entity-lock="props.entityLock"
        :on-before-click="() => canAddRow"
      />
    </template>
    <template #customToolbarDeleteButtonTemplate>
      <custom-toolbar-delete-button-template
        :master-entity-id="props.data?.id"
        :selected-rows-id="selectedRows.map(row => row.id)"
        :grid="gridRef"
        :entity-lock="props.entityLock"
        :on-delete="onBatchDeleteDocumentRowsAsync"
        :disabled="!canAddRow || selectedRows.some(row => row.confirmed_at)"
      />
    </template>
    <template #editButtonsTemplate="{ data: cellInfo }">
      <edit-lock-buttons-template
        entity-name="WarehouseDocument"
        :master-entity-id="props.data?.id"
        :cell-info="cellInfo"
        :grid="gridRef"
        :entity-lock="props.entityLock"
        :buttons="rowButtons"
      >
        <template #after-actions>
          <q-icon
            v-if="cellInfo.data.confirmed_at"
            name="mdi-check-circle-outline"
            color="positive"
            size="16px"
            class="q-mx-xs"
            role="img"
            :aria-label="i18n.t('warehouseDocument.confirmation.confirmed')"
          >
            <q-tooltip>{{ i18n.t('warehouseDocument.confirmation.confirmed') }}</q-tooltip>
          </q-icon>
        </template>
        <template #before-delete>
          <q-btn
            v-if="!cellInfo.data.confirmed_at"
            flat
            round
            dense
            icon="mdi-check-circle-outline"
            color="grey"
            size="sm"
            :disable="!canAddRow || isConfirming"
            :aria-label="i18n.t('warehouseDocument.confirmation.title')"
            @click.stop="previewConfirmation(cellInfo.data)"
          >
            <q-tooltip>{{ i18n.t('warehouseDocument.confirmation.title') }}</q-tooltip>
          </q-btn>
        </template>
      </edit-lock-buttons-template>
    </template>
    <template #generic-caption-template="{ data: captionInfo }">
      <div class="dx-form-group-caption-wrapper">
        <q-icon
          :name="captionInfo.name"
          class="dx-form-group-caption-icon"
        />
        <span class="dx-form-group-caption-text">{{ captionInfo.caption }}</span>
      </div>
    </template>
    <template #row-type-caption-template="{ data: captionInfo }">
      <document-row-type-toolbar
        :data="captionInfo"
        disabled
        :document-row-type="documentRowType"
        :allowed-row-types="[webSiteStore.documentRowTypesEnum.PRODUCT]"
        @value-changed="e => onDocumentRowTypeSelect(e.value)"
      />
    </template>
    <template #productEditor="{ data: cellInfo }">
      <product-drop-down-box
        ref="productDropDownRef"
        :brand="supplierBrand"
        :document-row-type="documentRowType"
        :disabled="!canEditRow"
        :data="cellInfo"
        :options="{ hideSupersession: true }"
        @value-changed="onProductValueChanged"
      />
    </template>
    <template #depositEditor="{ data: cellInfo }">
      <div>
        <deposit-drop-down-box
          :key="`${cellInfo.column.dataField}-${cellInfo.column.dataField === 'deposit' ? currentDeposit : currentDestinationDeposit}`"
          :data="cellInfo"
          :value="cellInfo.column.dataField === 'deposit' ? currentDeposit : currentDestinationDeposit"
          :disabled="!canEditRow"
        />
      </div>
    </template>
    <template #depositTransferArrowTemplate>
      <div
        class="warehouse-transfer-arrow flex flex-center"
        aria-hidden="true"
      >
        <q-icon
          name="mdi-arrow-right-bold"
          color="blue"
          size="sm"
        />
      </div>
    </template>
    <template #stockInformationTemplate>
      <warehouse-document-row-stock-info
        :data="stockInfo"
        :preview="rowStockPreview"
        :position-editable="canEditStockPosition"
        :document-type="props.documentType"
        @position-changed="onStockPositionChanged"
      />
    </template>
    <template #otherDepositsTemplate>
      <document-row-other-deposits
        :deposits="availableOtherDeposits"
        :current-deposit="currentDeposit"
        :disabled="!canEditRow"
        @deposit-selected="onOtherDepositSelected"
      />
    </template>
  </dx-data-grid>
  <dx-popup
    :visible="!!confirmation"
    :title="i18n.t('warehouseDocument.confirmation.title')"
    width="900px"
    max-width="95vw"
    height="auto"
    max-height="90vh"
    :hide-on-outside-click="false"
    :show-close-button="!isConfirming"
    @hiding="onConfirmationHiding"
  >
    <template #content>
      <div
        v-if="confirmation"
        class="q-pa-lg"
      >
        <div>
          {{ i18n.t('warehouseDocument.confirmation.expected') }}: {{ confirmation.expected_quantity }} {{ confirmation.unit }}
        </div>
        <dx-number-box
          :value="confirmationQuantity == null || confirmationQuantity === '' ? null : Number(confirmationQuantity)"
          :min="0"
          :step="1"
          :show-spin-buttons="false"
          format="#,##0.###"
          :disabled="isConfirming"
          value-change-event="input change"
          class="q-mt-md"
          @value-changed="e => { if (e.event) updateConfirmationQuantity(e.value) }"
        />
        <section class="q-mt-lg">
          <div class="text-primary text-weight-bold q-pb-sm">
            {{ i18n.t('common.stock') }}
          </div>
          <q-separator />
          <div class="row items-center q-col-gutter-md q-py-md">
            <div :class="isTransfer ? 'col-5' : 'col-12'">
              <dx-text-box
                :value="depositLabel(confirmationSourceDeposit)"
                :label="i18n.t(isTransfer ? 'warehouseDocument.sourceDeposit' : 'common.deposit')"
                label-mode="outside"
                :read-only="true"
              />
            </div>
            <template v-if="isTransfer">
              <div class="col-2 text-center">
                <q-icon
                  name="mdi-arrow-right-bold"
                  color="primary"
                  size="sm"
                />
              </div>
              <div class="col-5">
                <dx-text-box
                  :value="depositLabel(confirmationDestinationDeposit)"
                  :label="i18n.t('warehouseDocument.destinationDeposit')"
                  label-mode="outside"
                  :read-only="true"
                />
              </div>
            </template>
          </div>
          <div class="text-primary text-weight-bold q-pb-sm">
            {{ i18n.t('common.info', 2) }}
          </div>
          <q-separator class="q-mb-sm" />
          <document-row-stock-info
            :data="confirmationStockInfo"
            :changes="confirmationStockChanges"
            missing-as-zero
            :show-secondary="isTransfer"
            :show-settings="false"
          />
        </section>
      </div>
    </template>
    <dx-toolbar-item
      widget="dxButton"
      toolbar="bottom"
      location="after"
      :options="cancelConfirmationButton"
    />
    <dx-toolbar-item
      widget="dxButton"
      toolbar="bottom"
      location="after"
      :options="confirmConfirmationButton"
    />
  </dx-popup>
</template>

<style scoped>
.warehouse-transfer-arrow {
  height: 40px;
}
</style>
